import { Bot, ExternalLink, History, ShieldAlert, User, Cog } from "lucide-react";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { useHistoriqueArticle } from "@/hooks/useArticles";
import type { EntreeHistorique, MetadonneesNewsroom } from "@/types/article.types";

const STATUT_NEWSROOM_LABEL: Record<string, string> = {
  ai_draft: "Brouillon IA",
  editorial_review: "Revue éditoriale IA",
  ready_for_human_review: "Prêt pour validation humaine",
  approved: "Approuvé",
  published: "Publié",
  needs_revision: "Révision demandée",
  rejected: "Rejeté",
  archived: "Archivé",
};

const ACTION_LABEL: Record<string, string> = {
  CREATION: "Création",
  DEPOT_NEWSROOM: "Dépôt par la newsroom",
  MISE_A_JOUR_NEWSROOM: "Mise à jour par la newsroom",
  MODIFICATION: "Modification",
  SOUMISSION_VALIDATION: "Soumis à validation",
  RESOUMISSION: "Resoumis",
  DEMANDE_REVISION: "Révision demandée",
  REJET: "Rejet définitif",
  DEROGATION_DROIT_REPONSE: "Dérogation : droit de réponse en attente",
  PUBLICATION: "Publication",
  PUBLICATION_PROGRAMMEE: "Publication programmée",
  SUPPRESSION: "Suppression",
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

function Champ({ label, valeur }: { label: string; valeur?: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <div className="text-sm text-foreground">{valeur ?? "—"}</div>
    </div>
  );
}

const ouiNon = (v?: boolean | null) => (v == null ? "—" : v ? "Oui" : "Non");

/** Bandeau bloquant affiché en tête de fiche quand un droit de réponse est encore attendu. */
export function AlerteDroitDeReponse({ newsroom }: { newsroom?: MetadonneesNewsroom | null }) {
  if (!newsroom?.droitDeReponseEnAttente) return null;
  return (
    <div className="flex items-start gap-3 rounded-md border border-alert/30 bg-alert/5 p-4">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-alert" />
      <div className="text-sm">
        <p className="font-semibold text-alert">Droit de réponse requis — en attente</p>
        <p className="text-foreground/80">
          La publication est bloquée tant que la fiche de vérification n'est pas mise à jour. Une publication en
          dérogation reste possible ; elle sera tracée dans l'historique.
        </p>
      </div>
    </div>
  );
}

/** Données transmises par la newsroom IA : traçabilité, fiche de vérification, audit de la chaîne IA. */
export function NewsroomPanel({ newsroom }: { newsroom: MetadonneesNewsroom }) {
  const v = newsroom.verification;
  const sources = newsroom.sources ?? [];
  const illus = newsroom.illustration;

  return (
    <ModulePanel
      title="Newsroom IA"
      description={`Réf. ${newsroom.externalId} · ${STATUT_NEWSROOM_LABEL[newsroom.statutNewsroom] ?? newsroom.statutNewsroom}`}
    >
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Champ label="Type" valeur={newsroom.typeArticleEditorial} />
          <Champ label="Desk" valeur={newsroom.desk} />
          <Champ label="Priorité" valeur={newsroom.priorite} />
          <Champ label="Région" valeur={newsroom.region} />
          <Champ label="Déposé le" valeur={fmtDate(newsroom.creeLe)} />
          <Champ label="Soumis le" valeur={fmtDate(newsroom.soumisLe)} />
        </div>

        <section>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Sources ({sources.length})
          </h4>
          {sources.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune source transmise.</p>
          ) : (
            <ul className="space-y-2">
              {sources.map((s, i) => (
                <li key={i} className="rounded-md border border-border bg-surface p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{s.name}</span>
                    {s.type && (
                      <span className="rounded bg-background px-1.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                        {s.type}
                      </span>
                    )}
                    {s.url && (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-verified hover:underline break-all"
                      >
                        {s.url} <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {[s.publisher, s.publication_date && `publié ${s.publication_date}`, s.accessed_at && `consulté ${fmtDate(s.accessed_at)}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {s.note && <p className="mt-1 text-xs italic text-foreground/80">Note interne : {s.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Fiche de vérification</h4>
          {!v ? (
            <p className="text-sm text-muted-foreground">Aucune fiche transmise.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Champ label="Vérifié par" valeur={v.checked_by} />
                <Champ label="Vérifié le" valeur={fmtDate(v.verified_at)} />
                <Champ label="Sources indépendantes" valeur={v.independent_sources_count} />
                <Champ label="Source primaire" valeur={ouiNon(v.primary_source_present)} />
                <Champ label="Affirmations vérifiées" valeur={v.claims_checked} />
                <Champ label="Confiance" valeur={v.confidence} />
                <Champ label="Risque juridique" valeur={v.legal_risk} />
                <Champ label="Risque sécurité" valeur={v.security_risk} />
                <Champ
                  label="Droit de réponse"
                  valeur={
                    v.right_of_reply_required ? (
                      <span className={newsroom.droitDeReponseEnAttente ? "font-semibold text-alert" : ""}>
                        Requis · {v.right_of_reply_status || "pending"}
                      </span>
                    ) : (
                      "Non requis"
                    )
                  }
                />
              </div>
              {!!v.unverified_claims?.length && (
                <div className="rounded-md border border-alert/30 bg-alert/5 p-3">
                  <p className="mb-1 text-xs font-semibold text-alert">Affirmations non vérifiées</p>
                  <ul className="list-disc pl-5 text-sm">
                    {v.unverified_claims.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {v.notes && <p className="text-sm text-foreground/80 whitespace-pre-wrap">{v.notes}</p>}
            </div>
          )}
        </section>

        {illus && (
          <section>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Illustration</h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Champ label="Légende" valeur={illus.caption} />
              <Champ label="Texte alternatif" valeur={illus.alt_text} />
              <Champ label="Crédit" valeur={illus.credit} />
              <Champ label="Source" valeur={illus.source} />
              <Champ label="Licence" valeur={illus.license} />
              <Champ label="Ayant droit" valeur={illus.rights_holder} />
              <Champ label="Générée par IA" valeur={illus.ai_generated ? illus.ai_label || "Oui" : "Non"} />
            </div>
          </section>
        )}

        {(newsroom.metadonneesIa || newsroom.metadonneesEditoriales) && (
          <details className="rounded-md border border-border bg-surface p-3">
            <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Métadonnées IA et éditoriales (audit)
            </summary>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all text-xs text-foreground/80">
              {JSON.stringify(
                { ai_metadata: newsroom.metadonneesIa, editorial_metadata: newsroom.metadonneesEditoriales },
                null,
                2,
              )}
            </pre>
          </details>
        )}
      </div>
    </ModulePanel>
  );
}

function IconeActeur({ type }: { type?: EntreeHistorique["typeActeur"] }) {
  if (type === "IA") return <Bot className="h-3.5 w-3.5" />;
  if (type === "SYSTEME") return <Cog className="h-3.5 w-3.5" />;
  return <User className="h-3.5 w-3.5" />;
}

/** Journal d'audit de l'article : qui (humain, IA, système) a fait quoi, et quand. */
export function HistoriquePanel({ articleId }: { articleId: number }) {
  const { data, isLoading } = useHistoriqueArticle(articleId);
  const entrees = data ?? [];

  return (
    <ModulePanel title="Historique" description="Journal d'audit de l'article">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : entrees.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <History className="h-4 w-4" /> Aucun événement enregistré.
        </p>
      ) : (
        <ol className="space-y-2">
          {entrees.map((e) => (
            <li key={e.id} className="flex items-start gap-3 text-sm">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface text-muted-foreground">
                <IconeActeur type={e.typeActeur} />
              </span>
              <div className="min-w-0">
                <p className="text-foreground">
                  <span className="font-medium">{ACTION_LABEL[e.action] ?? e.action}</span>
                  <span className="text-muted-foreground">
                    {" "}· {e.acteurNom ?? (e.typeActeur === "SYSTEME" ? "Système" : "—")}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">{fmtDate(e.creeLe)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </ModulePanel>
  );
}
